import { apiClient } from '@/lib/apiClient';
import { LeadCompany } from '@/services/leadCompaniesService';

// Lead API endpoints using the centralized apiClient

export const fetchLeadById = async (id: string) => {
  try {
    console.log(`🔍 Fetching lead details for ID: ${id}`);
    const response = await apiClient.get(`/leads/${id}`);
    
    // Handle potential datetime serialization issues
    if (response && typeof response === 'object') {
      // If the response contains datetime fields, ensure they're properly handled
      const processDateFields = (obj: any): any => {
        if (obj === null || obj === undefined) return obj;
        
        if (typeof obj === 'object' && !Array.isArray(obj)) {
          const processed = { ...obj };
          
          // Common datetime field names that might cause issues
          const dateFields = ['last_analyzed', 'analyzed_at', 'created_at', 'updated_at', 'date_created', 'date_modified'];
          
          for (const field of dateFields) {
            if (processed[field]) {
              try {
                // If it's already a valid date string, keep it
                if (typeof processed[field] === 'string') {
                  const date = new Date(processed[field]);
                  if (isNaN(date.getTime())) {
                    processed[field] = null;
                  }
                }
              } catch (error) {
                console.warn(`Date field ${field} processing error:`, error);
                processed[field] = null;
              }
            }
          }
          
          return processed;
        }
        
        return obj;
      };
      
      return processDateFields(response);
    }
    
    return response;
  } catch (error) {
    console.error('💥 Error fetching lead details:', error);
    
    // Check if it's a JSON serialization error and provide more context
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('JSON serializable') || errorMessage.includes('datetime')) {
      const enhancedError = new Error(`Backend datetime serialization error for lead ${id}. The API needs to convert datetime objects to strings before sending JSON response.`);
      enhancedError.name = 'SerializationError';
      throw enhancedError;
    }
    
    throw error;
  }
};

export const fetchLeadsByName = async (name: string) => {
  try {
    console.log(`🔍 Searching leads by name: ${name}`);
    const response = await apiClient.get(`/leads/name/${encodeURIComponent(name)}`);
    console.log(`🔍 Raw API Response:`, response);
    
    // Handle the actual API response structure {success: true, leads: [...]}
    if (response && response.success && Array.isArray(response.leads)) {
      console.log(`🔍 Found ${response.leads.length} leads in successful response`);
      return response.leads;
    }
    
    // Fallback for direct array response
    if (Array.isArray(response)) {
      console.log(`🔍 Direct array response with ${response.length} leads`);
      return response;
    }
    
    // Additional fallbacks
    if (response && response.data && Array.isArray(response.data)) {
      return response.data;
    }
    
    console.warn(`🔍 Unexpected response structure:`, response);
    return [];
  } catch (error) {
    console.error('💥 Error searching leads by name:', error);
    throw error;
  }
};

export const fetchLeadContacts = async (id: string) => {
  try {
    console.log(`🔍 Fetching contacts for lead ID: ${id} (using fetchLeadWithContacts)`);
    const response = await fetchLeadWithContacts(id);
    // Return just the contacts array for backward compatibility
    return response.contacts || [];
  } catch (error) {
    console.error('💥 Error fetching lead contacts:', error);
    throw error;
  }
};

export const fetchLeadActivities = async (id: string) => {
  try {
    console.log(`🔍 Fetching activities for lead ID: ${id}`);
    const response = await apiClient.get(`/leads/${id}/activities`);
    return response;
  } catch (error) {
    console.error('💥 Error fetching lead activities:', error);
    throw error;
  }
};

export const fetchLeadQualification = async (id: string) => {
  try {
    console.log(`🔍 Fetching qualification for lead ID: ${id}`);
    const response = await apiClient.get(`/leads/${id}/qualification`);
    return response;
  } catch (error) {
    console.error('💥 Error fetching lead qualification:', error);
    throw error;
  }
};

export const fetchLeadScore = async (id: string) => {
  try {
    console.log(`🔍 Fetching score for lead ID: ${id}`);
    const response = await apiClient.get(`/leads/${id}/score`);
    return response;
  } catch (error) {
    console.error('💥 Error fetching lead score:', error);
    throw error;
  }
};

export const createLead = async (leadData: Partial<LeadCompany>) => {
  try {
    console.log('🔄 Creating new lead:', leadData);
    const response = await apiClient.post('/leads', leadData);
    return response;
  } catch (error) {
    console.error('💥 Error creating lead:', error);
    throw error;
  }
};

export const updateLead = async (id: string, leadData: Partial<LeadCompany>) => {
  try {
    console.log(`🔄 Updating lead ID: ${id}`, leadData);
    const response = await apiClient.put(`/leads/${id}`, leadData);
    return response;
  } catch (error) {
    console.error('💥 Error updating lead:', error);
    throw error;
  }
};

export const deleteLead = async (id: string) => {
  try {
    console.log(`🗑️ Deleting lead ID: ${id}`);
    const response = await apiClient.delete(`/leads/${id}`);
    return response;
  } catch (error) {
    console.error('💥 Error deleting lead:', error);
    throw error;
  }
};

export const fetchLeadWithContacts = async (id: string, bypassCache: boolean = false) => {
  try {
    console.log(`🔍 Fetching lead with contacts for ID: ${id}${bypassCache ? ' (bypassing cache)' : ''}`);
    
    // Create unique request to bypass cache if needed
    const endpoint = `/leads/${id}/all/contacts`;
    const options = bypassCache ? {
      headers: {
        'X-Request-Key': `refresh-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }
    } : {};
    
    const response = await apiClient.request(endpoint, { method: 'GET', ...options });
    
    // Handle potential datetime serialization issues
    if (response && typeof response === 'object') {
      const processDateFields = (obj: any): any => {
        if (obj === null || obj === undefined) return obj;
        
        if (typeof obj === 'object' && !Array.isArray(obj)) {
          const processed = { ...obj };
          
          const dateFields = ['last_analyzed', 'analyzed_at', 'created_at', 'updated_at', 'date_created', 'date_modified'];
          
          for (const field of dateFields) {
            if (processed[field]) {
              try {
                if (typeof processed[field] === 'string') {
                  const date = new Date(processed[field]);
                  if (isNaN(date.getTime())) {
                    processed[field] = null;
                  }
                }
              } catch (error) {
                console.warn(`Date field ${field} processing error:`, error);
                processed[field] = null;
              }
            }
          }
          
          return processed;
        }
        
        return obj;
      };
      
      const processedResponse = processDateFields(response);
      
      // Process contacts array if present
      if (processedResponse.contacts && Array.isArray(processedResponse.contacts)) {
        processedResponse.contacts = processedResponse.contacts.map(processDateFields);
      }
      
      return processedResponse;
    }
    
    return response;
  } catch (error) {
    console.error('💥 Error fetching lead with contacts:', error);
    throw error;
  }
};

export const fetchAllLeads = async (params?: { status?: string; country?: string; search?: string; page?: number; limit?: number; assigned_to?: number; current_stage?: string; stale_days?: number; no_activity?: boolean; has_phone?: boolean; has_email?: boolean; min_score?: number; sort_by?: string; sort_order?: string }) => {
  try {
    console.log(`🔍 Fetching all leads with params:`, params);
    
    // Build query string if params provided
    let url = '/leads';
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params.assigned_to) {
        queryParams.append('assigned_to', params.assigned_to.toString());
      }
      if (params.status && params.status !== 'all') {
        queryParams.append('status', params.status);
      }
      if (params.country && params.country !== 'all') {
        queryParams.append('country', params.country);
      }
      if (params.current_stage && params.current_stage !== 'all') {
        queryParams.append('current_stage', params.current_stage);
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }
      if (params.stale_days) {
        queryParams.append('stale_days', params.stale_days.toString());
      }
      if (params.no_activity) {
        queryParams.append('no_activity', 'true');
      }
      if (params.has_phone === false) {
        queryParams.append('has_phone', 'false');
      }
      if (params.has_email === false) {
        queryParams.append('has_email', 'false');
      }
      if (params.min_score) {
        queryParams.append('min_score', params.min_score.toString());
      }
      if (params.sort_by) {
        queryParams.append('sort_by', params.sort_by);
      }
      if (params.sort_order) {
        queryParams.append('sort_order', params.sort_order);
      }
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
    }
    
    // Use apiClient for consistent HTTPS and authorization
    const data = await apiClient.get(url);
    console.log(`🔍 API client response:`, data);
    
    // Handle the API response structure - return FULL response with pagination metadata
    if (data && data.success && data.leads) {
      console.log(`🔍 Returning full response with ${data.leads.length} leads and pagination metadata`);
      console.log(`🔍 Pagination: page ${data.page}/${data.total_pages}, total: ${data.total}`);
      // Return the entire response object to preserve pagination metadata
      return {
        success: data.success,
        leads: data.leads,
        page: data.page || 1,
        limit: data.limit || 10,
        total: data.total || 0,
        total_pages: data.total_pages || 1,
        has_prev: data.has_prev || false,
        has_next: data.has_next || false
      };
    }
    
    // Fallback for different response structures
    if (Array.isArray(data)) {
      console.log(`🔍 Array response with ${data.length} items - wrapping with default pagination`);
      return {
        success: true,
        leads: data,
        page: 1,
        limit: data.length,
        total: data.length,
        total_pages: 1,
        has_prev: false,
        has_next: false
      };
    }
    
    console.log(`🔍 No leads found, returning empty response`);
    return {
      success: true,
      leads: [],
      page: 1,
      limit: 10,
      total: 0,
      total_pages: 0,
      has_prev: false,
      has_next: false
    };
  } catch (error) {
    console.error('💥 Error fetching all leads:', error);
    throw error;
  }
};
